import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Divider,
  Stack,
  TextField,
  Link,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useAuth } from '../../contexts/AuthContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  AuthProvider,
  OAuthActionType,
  OAuthSuccessResponse,
  OAuthErrorResponse,
  OAuthResponse,
} from '@avanzu/types';
import SadAlert from '../../components/SadAlert';
import AvanzuHeader from '../../components/AvanzuHeader';

const loginSchema = yup.object({
  email: yup.string().email('Please enter a valid email address').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 6 characters')
    .required('Password is required'),
});

type LoginFormData = yup.InferType<typeof loginSchema>;

interface LoginError {
  error: string;
  field?: 'email' | 'password' | 'general';
}

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState<LoginError | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  useEffect(() => {
    // Listen for messages from popup
    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data as OAuthResponse;

      // Handle new structured OAuth responses
      if (data && typeof data.success !== 'undefined') {
        if (data.success) {
          // Handle successful OAuth login
          const successData = data as OAuthSuccessResponse;

          if (successData.action === OAuthActionType.LOGIN && successData.token) {
            try {
              setLoginError(null);
              await login(successData.token);
              const returnUrl = successData.returnUrl || '/dashboard';
              navigate(returnUrl);
            } catch (error) {
              console.error('Failed to process login:', error);
              setLoginError({
                error: 'Failed to complete login. Please try again.',
                field: 'general',
              });
            }
          }
        } else {
          // Handle OAuth error
          const errorData = data as OAuthErrorResponse;
          console.error('OAuth error:', errorData.message);
          setLoginError({
            error: errorData.message || 'OAuth login failed. Please try again.',
            field: 'general',
          });
        }
        return;
      }

      // Keep legacy support for backward compatibility
      if (event.data.type === 'oauth-success' && event.data.token) {
        try {
          setLoginError(null);
          await login(event.data.token);
          const returnUrl = event.data.returnUrl || '/dashboard';
          navigate(returnUrl);
        } catch (error) {
          console.error('Failed to process login:', error);
          setLoginError({
            error: 'Failed to complete login. Please try again.',
            field: 'general',
          });
        }
      } else if (event.data.type === 'oauth-error') {
        console.error('OAuth error:', event.data.error);
        setLoginError({
          error: event.data.message || event.data.error || 'OAuth login failed. Please try again.',
          field: 'general',
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [login, navigate]);

  const handleOAuthLogin = (provider: AuthProvider) => {
    setLoginError(null); // Clear any previous errors

    const width = 500;
    const height = provider === AuthProvider.XERO ? 800 : 600; // xero wants a bigger one
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${process.env.REACT_APP_API_URL}/auth/oauth/${provider}?popup=true&action=login`,
      `${provider}-oauth`,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
    );

    // Check if popup was blocked
    if (!popup || popup.closed) {
      setLoginError({
        error: 'Please allow popups for this site to sign in.',
        field: 'general',
      });
      return;
    }

    // Check if popup closes without completing OAuth
    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        // Add a small delay to allow message processing
        // setTimeout(() => {
        //   // Only show error if no success message was received
        //   if (!loginError) {
        //     setLoginError({
        //       error: 'Sign in was cancelled or failed. Please try again.',
        //       field: 'general',
        //     });
        //   }
        // }, 500);
      }
    }, 1000);
  };

  const handleGoogleLogin = () => handleOAuthLogin(AuthProvider.GOOGLE);
  const handleXeroLogin = () => handleOAuthLogin(AuthProvider.XERO);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoginError(null); // Clear any previous errors

      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData: LoginError = await response.json();

        // Handle field-specific errors
        if (errorData.field === 'email') {
          setError('email', {
            type: 'server',
            message: errorData.error,
          });
        } else if (errorData.field === 'password') {
          setError('password', {
            type: 'server',
            message: errorData.error,
          });
        } else {
          // Handle general errors
          setLoginError(errorData);
        }
        return;
      }

      const result = await response.json();

      // Pass user, token, and organisations array to login method
      await login(result.token, result.user, result.organisations);

      const returnUrl = result.returnUrl || '/dashboard';
      navigate(returnUrl);
    } catch (error) {
      console.error('Login error:', error);
      setLoginError({
        error: 'Network error. Please check your connection and try again.',
        field: 'general',
      });
    }
  };

  return (
    <Container maxWidth="xs">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" align="center" gutterBottom>
            Login to Avanzu
          </Typography>

          {/* Display general error messages */}
          {loginError && loginError.field === 'general' && <SadAlert>{loginError.error}</SadAlert>}

          <Stack spacing={2} sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin}
              disabled={isSubmitting}
              sx={{
                bgcolor: '#4285F4',
                '&:hover': { bgcolor: '#357ae8' },
              }}
            >
              Continue with Google
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={handleXeroLogin}
              disabled={isSubmitting}
              sx={{
                bgcolor: '#13B5EA',
                '&:hover': { bgcolor: '#0e8bb5' },
              }}
            >
              Continue with Xero
            </Button>
          </Stack>

          <Divider sx={{ my: 3, color: 'text.secondary' }}>OR</Divider>

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register('email')}
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={isSubmitting}
              />

              <TextField
                fullWidth
                label="Password"
                type="password"
                {...register('password')}
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isSubmitting}
              />

              <Box sx={{ textAlign: 'right' }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  underline="none"
                  sx={{ color: 'primary.main' }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{ mt: 2 }}
              >
                {isSubmitting ? 'Signing in...' : 'Sign In'}
              </Button>
            </Stack>
          </Box>

          <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 3 }}>
            Don't have an account?{' '}
            <Link component={RouterLink} to="/signup" underline="none">
              Create account
            </Link>
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
